import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import ExternalLink from '@groovehq/internal-design-system/lib/assets/icons/ExternalLink'
import { KeyValue } from 'components/KeyValues'
import NavigatableIntegrationWidgetCard from 'components/integrations/NavigatableIntegrationWidgetCard'
import {
  FULFILLMENT_DISPLAY_STATUS_TEXT,
  ORDER_DISPLAY_FINANCIAL_STATUS_TEXT,
} from 'ducks/integrations/shopify/constants'
import { toDate } from 'util/date'
import { shopifyMoneyString } from 'util/currency'
import { selectOrderForId } from 'ducks/integrations/shopify/selectors'
import CancelOrderButton from './CancelOrderButton'
import RefundOrderButton from './RefundOrderButton'
import DuplicateOrderButton from './DuplicateOrderButton'

const LineItem = ({ lineItem, integration }) => {
  const storeDomain = integration.storeDomain
  const { product } = lineItem
  let url
  if (product) {
    const productId = product.id.replace(/[^\d]*/, '')
    url = `https://${storeDomain}.myshopify.com/admin/products/${productId}`
  }
  const discountedShopMoney = lineItem.discountedUnitPriceSet.shopMoney
  const totalShopMoney = lineItem.discountedTotalSet.shopMoney
  const itemText = useMemo(
    () => {
      return `${shopifyMoneyString(discountedShopMoney)} ${String.fromCharCode(
        215
      )} ${lineItem.currentQuantity}`
    },
    [discountedShopMoney, lineItem]
  )

  const total = discountedShopMoney.amount * lineItem.currentQuantity

  return (
    <tbody className="shopifyV2-order-table-lineItem">
      <tr>
        <th colSpan="2">
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {lineItem.name}
              <ExternalLink className="shopifyV2-externalLink-icon" />
            </a>
          )}
          {!url && lineItem.name}
        </th>
      </tr>
      <tr className="shopifyV2-currency">
        <th scope="row" className="shopifyV2-order-table-itemText">
          {itemText}
        </th>
        <td className="grui text-right">
          {shopifyMoneyString({
            amount: total,
            currencyCode: totalShopMoney.currencyCode,
          })}
        </td>
      </tr>
    </tbody>
  )
}

const FulfillmentRow = ({ fulfillment: { trackingInfo } }) => {
  if (!trackingInfo) return null
  if (trackingInfo.length === 1) {
    trackingInfo.push(trackingInfo[0])
    trackingInfo.push(trackingInfo[0])
  }

  return (
    <>
      {trackingInfo.map(({ company, url, number }) => (
        <div>
          {company},{' '}
          <a href={url} target="_blank" rel="noopener noreferrer">
            {number}
            <ExternalLink className="shopifyV2-externalLink-icon" />
          </a>
        </div>
      ))}
    </>
  )
}

const ConditionalRow = ({ obj, text, negative = false }) => {
  const { shopMoney } = obj
  if (!shopMoney) return null
  if (!shopMoney.amount) return null
  if (parseFloat(shopMoney.amount) === 0) return null

  const { amount } = shopMoney

  return (
    <tr>
      <th scope="row">
        <KeyValue title={text} missingValue={null} />
      </th>
      <td className="shopifyV2-currency grui text-right">
        {shopifyMoneyString({
          ...shopMoney,
          amount: negative ? Math.abs(amount) * -1 : amount,
        })}
      </td>
    </tr>
  )
}

export default function ShopifyDetail({
  navigatableState,
  doNavigatableClearState,
}) {
  const { orderId = null, integration = {} } = navigatableState || {}

  const orderShortId = orderId && orderId.split('/').pop()
  // we pass the order id and then get it from the state - this allows the id to be stored
  // in the url and survices a refresh
  const order = useSelector(state =>
    selectOrderForId(state, { orderId: orderShortId })
  )

  const lineItems = useMemo(
    () => {
      return order ? order.lineItems?.edges.map(edge => edge.node) : []
    },
    [order]
  )

  const cancelledAtString = useMemo(
    () => {
      if (order && order.isCancelled) {
        return toDate(order.cancelledAt).toLocaleDateString('en-US')
      }
      return null
    },
    [order]
  )

  if (!navigatableState) return null
  if (!order) return null

  const orderUrl = `https://${
    integration.storeDomain
  }.myshopify.com/admin/orders/${orderShortId}`

  return (
    <NavigatableIntegrationWidgetCard.Detail
      onBack={doNavigatableClearState}
      title={order.name}
    >
      <div className="grui p-7 shopifyV2-order-data">
        <KeyValue
          title="Order ID"
          value={
            <a href={orderUrl} target="_blank" rel="noopener noreferrer">
              {orderShortId}
              <ExternalLink className="shopifyV2-externalLink-icon" />
            </a>
          }
        />
        <KeyValue title="Date" value={order.date} />
        <KeyValue
          title="Payment status"
          value={
            ORDER_DISPLAY_FINANCIAL_STATUS_TEXT[order.displayFinancialStatus]
          }
        />
        <KeyValue
          title="Fulfillment status"
          value={
            FULFILLMENT_DISPLAY_STATUS_TEXT[order.displayFulfillmentStatus]
          }
        />
        {order.isCancelled && (
          <KeyValue title="Cancelled" value={cancelledAtString} />
        )}
        {order.requiresShipping &&
          order.fulfillments &&
          order.fulfillments.length > 0 && (
            <KeyValue
              title="Shipping"
              value={
                <>
                  {order.fulfillments.map(fulfillment => (
                    <FulfillmentRow
                      fulfillment={fulfillment}
                      key={fulfillment.id}
                    />
                  ))}
                </>
              }
            />
          )}
      </div>
      <div className="shopifyV2-header-label">Order details</div>
      <div className="grui p-7">
        <table className="shopifyV2-order-table">
          {lineItems.map(lineItem => (
            <LineItem
              lineItem={lineItem}
              integration={integration}
              key={lineItem.id}
            />
          ))}
          <tbody className="shopifyV2-order-table-sub">
            {order.requiresShipping && (
              <ConditionalRow
                text="Shipping"
                obj={order.totalShippingPriceSet}
              />
            )}
            <ConditionalRow
              text="Subtotal"
              obj={order.currentSubtotalPriceSet}
            />
            <ConditionalRow text="Tax" obj={order.currentTotalTaxSet} />
            <ConditionalRow
              text="Discount"
              obj={order.currentTotalDiscountsSet}
              negative
            />
          </tbody>
          <tfoot>
            <tr>
              <td>
                <KeyValue title="Total" missingValue={null} />
              </td>
              <td className="grui text-right shopifyV2-currency">
                {shopifyMoneyString(order.currentTotalPriceSet.shopMoney)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="shopifyV2-footer">
        {order.refundable && (
          <RefundOrderButton orderId={orderShortId} integration={integration} />
        )}
        <DuplicateOrderButton order={order} integration={integration} />
        {order.isCancellable && (
          <CancelOrderButton
            integration={integration}
            orderId={orderShortId}
            unpaid={order.unpaid}
            refundAmount={order.refundAmount}
            currencyCode={order.refundCurrencyCode}
          />
        )}
      </div>
    </NavigatableIntegrationWidgetCard.Detail>
  )
}
